
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'motion/react';
import { View } from '../types';
import ScrudixLogo from './ScrudixLogo';
import { useTheme } from '../services/ThemeContext';
import Button from './ui/Button';

interface HeaderProps {
  onNavigate: (view: View) => void;
  currentView: View;
  isAuthenticated?: boolean;
  onDashboard?: () => void;
  hidden?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onNavigate, currentView, isAuthenticated, onDashboard, hidden = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [infoDropdownOpen, setInfoDropdownOpen] = useState(false);
  const infoDropdownRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const { isDark, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let active = false;
    const handleScroll = () => {
      const sy = window.scrollY;
      if (progressBarRef.current) {
        const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (totalHeight > 0) {
          const pct = Math.min(1, Math.max(0, sy / totalHeight));
          progressBarRef.current.style.transform = `scaleX(${pct.toFixed(4)})`;
        }
      }
      const nextScrolled = active ? sy > 12 : sy > 36;
      if (nextScrolled !== active) {
        active = nextScrolled;
        setScrolled(nextScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentView]);

  // Close on escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Prevent body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleNav = useCallback((view: View) => {
    setMobileMenuOpen(false);
    setInfoDropdownOpen(false);
    onNavigate(view);
  }, [onNavigate]);

  const scrollToSection = useCallback((id: string) => {
    setMobileMenuOpen(false);
    setInfoDropdownOpen(false);
    onNavigate(View.LANDING);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  }, [onNavigate]);

  const navLinkClass = (isActive: boolean) =>
    `font-extrabold rounded-full transition-all duration-300 relative group flex items-center justify-center cursor-pointer select-none ${
      scrolled ? 'px-3.5 xl:px-4 py-2.5 text-xs sm:text-sm' : 'px-4.5 xl:px-5 py-3 text-sm'
    } ${
      isActive
        ? 'bg-zinc-950 text-white dark:bg-white dark:text-zinc-950 shadow-md shadow-zinc-950/20 dark:shadow-white/20 font-black scale-[1.02] ring-1 ring-white/20 dark:ring-black/10'
        : 'text-zinc-800 hover:text-black dark:text-zinc-200 dark:hover:text-white hover:bg-white/80 dark:hover:bg-white/15 hover:shadow-lg hover:shadow-violet-500/15 hover:-translate-y-1 active:translate-y-0 active:scale-95'
    }`;

  const lumiNavClass = (isActive: boolean) =>
    `font-extrabold rounded-full transition-all duration-300 relative group overflow-hidden flex items-center gap-2 cursor-pointer select-none ${
      scrolled ? 'px-3.5 xl:px-4 py-2.5 text-xs sm:text-sm' : 'px-4.5 xl:px-5 py-3 text-sm'
    } ${
      isActive
        ? 'text-zinc-950 dark:text-zinc-100 font-black scale-[1.02]'
        : 'text-zinc-800 hover:text-black dark:text-zinc-200 dark:hover:text-white hover:bg-white/80 dark:hover:bg-white/15 hover:shadow-lg hover:shadow-pink-500/15 hover:-translate-y-1 active:translate-y-0 active:scale-95'
    }`;

  const lumiMobileClass = (isActive: boolean) =>
    `w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all flex items-center gap-2 relative overflow-hidden bg-transparent ${
      isActive
        ? 'text-zinc-900 dark:text-zinc-100'
        : 'text-zinc-600 hover:text-black dark:text-zinc-300 dark:hover:text-white'
    }`;

  const lumiLabelClass = (isActive: boolean) =>
    isActive
      ? ''
      : 'bg-[linear-gradient(100deg,#22d3ee_5%,#a78bfa_38%,#f472b6_68%,#facc15_100%)] bg-[length:220%_100%] bg-clip-text text-transparent animate-lumi-neon-shine [text-shadow:0_0_8px_rgba(56,189,248,0.22)] [will-change:background-position,opacity] [transform:translateZ(0)] [backface-visibility:hidden]';

  const lumiIconClass = (isActive: boolean) =>
    isActive ? '' : 'text-violet-500 dark:text-violet-300 animate-lumi-icon-breathe [transform:translateZ(0)] [backface-visibility:hidden]';

  return (
    <>
      <style>{`
        @keyframes lumiNeonShine {
          0% { background-position: 0% 50%; opacity: 0.92; }
          50% { background-position: 100% 50%; opacity: 1; }
          100% { background-position: 0% 50%; opacity: 0.92; }
        }
        .animate-lumi-neon-shine {
          animation: lumiNeonShine 6.6s linear infinite;
        }
        @keyframes lumiIconBreathe {
          0%, 100% {
            opacity: 0.9;
            filter: drop-shadow(0 0 4px rgba(167,139,250,0.3));
          }
          50% {
            opacity: 1;
            filter: drop-shadow(0 0 7px rgba(167,139,250,0.42));
          }
        }
        .animate-lumi-icon-breathe {
          animation: lumiIconBreathe 4.8s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-lumi-neon-shine,
          .animate-lumi-icon-breathe {
            animation: none;
          }
        }
        .fixed-header-container {
          will-change: padding, margin, background-color, border-radius, box-shadow, max-width, transform;
          transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
          transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .fixed-header-nav, .fixed-header-actions {
          transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
      
      {/* Scroll progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[110] h-[2.5px] bg-transparent pointer-events-none">
        <div 
          ref={progressBarRef}
          className="h-full w-full origin-left bg-gradient-to-r from-cyan-400 via-violet-500 via-pink-500 to-amber-400 transition-transform duration-75 ease-out shadow-[0_0_12px_rgba(167,139,250,0.9)]"
          style={{ transform: 'scaleX(0)' }}
        />
      </div>

      <header className={`fixed top-0 left-0 right-0 z-[100] w-full flex justify-center pointer-events-none transition-transform duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${hidden ? '-translate-y-full' : 'translate-y-0'}`}>
        <div className={`fixed-header-container w-full flex items-center justify-between flex-nowrap pointer-events-auto transition-all duration-500 ${
          scrolled
            ? 'mt-3.5 md:mt-4.5 rounded-full max-w-[95%] xl:max-w-7xl mx-auto px-5 sm:px-7 md:px-9 py-3 md:py-3.5 backdrop-blur-2xl backdrop-saturate-150 ' +
              (isDark
                ? 'bg-[linear-gradient(to_right,rgba(88,28,135,0.35)_0%,rgba(88,28,135,0.15)_6%,rgba(9,9,11,0.92)_10%,rgba(9,9,11,0.92)_90%,rgba(113,63,18,0.15)_94%,rgba(113,63,18,0.35)_100%),linear-gradient(to_right,rgba(168,85,247,0.6)_0%,rgba(168,85,247,0.4)_6%,rgba(255,255,255,0.15)_10%,rgba(255,255,255,0.15)_90%,rgba(234,179,8,0.4)_94%,rgba(234,179,8,0.6)_100%)] [background-origin:border-box] [background-clip:padding-box,border-box] border-2 border-transparent text-zinc-100 shadow-[0_14px_45px_rgba(0,0,0,0.75),_0_0_20px_rgba(168,85,247,0.1)]'
                : 'bg-[linear-gradient(to_right,rgba(238,232,255,0.4)_0%,rgba(238,232,255,0.25)_18%,rgba(255,255,255,0.75)_25%,rgba(255,255,255,0.75)_75%,rgba(254,249,195,0.25)_82%,rgba(254,249,195,0.4)_100%),linear-gradient(to_right,rgba(216,180,254,0.6)_0%,rgba(216,180,254,0.45)_18%,rgba(255,255,255,0.85)_25%,rgba(255,255,255,0.85)_75%,rgba(254,240,138,0.45)_82%,rgba(254,240,138,0.6)_100%)] [background-origin:border-box] [background-clip:padding-box,border-box] border-2 border-transparent text-zinc-900 shadow-[0_12px_40px_rgba(216,180,254,0.18),_0_12px_40px_rgba(254,240,138,0.18),_inset_0_1.5px_0_rgba(255,255,255,0.95)]')
            : 'mt-0 rounded-none max-w-full px-6 sm:px-9 md:px-11 lg:px-14 py-4 md:py-6 border-0 shadow-none bg-transparent backdrop-blur-md text-zinc-900 dark:text-white'
        }`}>
        {/* Logo — left corner */}
        <button
          type="button"
          aria-label="Go to Scrudix home"
          className={`flex items-center cursor-pointer group appearance-none border-0 bg-transparent p-0 shrink-0 transition-all duration-500 ${scrolled ? 'gap-2 scale-95' : 'gap-2 sm:gap-3 scale-100'}`}
          onClick={() => handleNav(View.LANDING)}
        >
          <ScrudixLogo size={scrolled ? 'sm' : 'md'} className="group-hover:scale-115 group-hover:-rotate-6 transition-all duration-300" />
          <span className={`font-black tracking-tight transition-all duration-300 group-hover:text-violet-600 dark:group-hover:text-violet-300 ${scrolled ? 'text-sm sm:text-base' : 'text-lg sm:text-xl'}`}>Scrudix</span>
          <span className={`hidden sm:inline-flex font-black uppercase tracking-widest rounded-full border transition-all duration-300 group-hover:scale-105 group-hover:bg-violet-500/25 group-hover:border-violet-500/50 group-hover:shadow-[0_0_10px_rgba(167,139,250,0.4)] ${
            scrolled 
              ? 'px-1.5 py-0 text-[8px] bg-violet-500/15 text-violet-400 border-violet-500/25' 
              : 'px-2 py-0.5 text-[10px] bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-200 dark:border-violet-500/20'
          }`}>
            Beta
          </span>
        </button>

        {/* Desktop Navigation — left-aligned next to logo */}
        <nav className={`fixed-header-nav hidden lg:flex items-center flex-nowrap ${
          scrolled ? 'gap-2 ml-3' : 'gap-3 ml-4'
        }`}>
          {/* Lumi */}
          <button
            onClick={() => handleNav(View.LUMI)}
            aria-label="Open Lumi"
            className={lumiNavClass(currentView === View.LUMI)}
          >
            <span className="relative z-10 flex items-center gap-2">
              <span className={lumiLabelClass(currentView === View.LUMI)}>Lumi</span>
            </span>
          </button>

          {/* Information dropdown */}
          <div
            ref={infoDropdownRef}
            className="relative"
            onMouseEnter={() => setInfoDropdownOpen(true)}
            onMouseLeave={() => setInfoDropdownOpen(false)}
          >
            <button
              className={`${navLinkClass(
                [View.EXTENSIONS, View.DEMO, View.DOCUMENTATION, View.COMMANDS, View.BLOG].includes(currentView)
              )} flex items-center gap-1.5`}
            >
              Information
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${infoDropdownOpen ? 'rotate-180' : ''}`}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {/* Invisible bridge to prevent gap between button and panel */}
            <div className={`absolute top-full left-1/2 -translate-x-1/2 w-full h-3 ${infoDropdownOpen ? 'pointer-events-auto' : 'pointer-events-none'}`} />
            {/* Dropdown panel — 2×2 grid */}
            <div className={`absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-[340px] rounded-2xl shadow-2xl border border-white/60 dark:border-white/10 bg-white/80 dark:bg-zinc-900/85 backdrop-blur-xl overflow-hidden transition-all duration-200 origin-top ${infoDropdownOpen ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'}`}>
              {/* Panel header */}
              <div className="px-4 pt-3 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500">More pages</p>
              </div>
              <div className="p-2 grid grid-cols-2 gap-1">
                {[
                  { label: 'Extensions', desc: 'IDE integrations', view: View.EXTENSIONS, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a2 2 0 0 0 0-4h-1.26a6 6 0 0 0-.84-2.03l.9-.9a2 2 0 1 0-2.83-2.83l-.9.9A6 6 0 0 0 13 2.26V1a2 2 0 0 0-4 0v1.26a6 6 0 0 0-2.03.84l-.9-.9a2 2 0 1 0-2.83 2.83l.9.9A6 6 0 0 0 2.26 11H1a2 2 0 0 0 0 4h1.26a6 6 0 0 0 .84 2.03l-.9.9a2 2 0 1 0 2.83 2.83l.9-.9A6 6 0 0 0 11 21.74V23a2 2 0 0 0 4 0v-1.26a6 6 0 0 0 2.03-.84l.9.9a2 2 0 1 0 2.83-2.83l-.9-.9A6 6 0 0 0 21.74 13H23a2 2 0 0 0 0-4h-1.26Z"/><path d="M9.5 9.5h5v5h-5z"/></svg> },
                  { label: 'Live Demo', desc: 'See it in action', view: View.DEMO, icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21" /></svg> },
                  { label: 'Documentation', desc: 'Guides & API', view: View.DOCUMENTATION, icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
                  { label: 'Blog', desc: 'Insights & guides', view: View.BLOG, icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg> },
                  { label: 'Commands', desc: 'CLI reference', view: View.COMMANDS, icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg> },
                ].map(({ label, desc, view, icon }) => (
                  <button
                    key={view}
                    onClick={() => handleNav(view)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-colors group ${
                      currentView === view
                        ? 'bg-zinc-100 dark:bg-white/10'
                        : 'hover:bg-zinc-50 dark:hover:bg-white/5'
                    }`}
                  >
                    <span className={`shrink-0 flex items-center justify-center w-8 h-8 rounded-lg ${currentView === view ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700'} transition-colors`}>{icon}</span>
                    <span className="flex flex-col items-start leading-none gap-0.5">
                      <span className={`font-semibold text-[13px] ${currentView === view ? 'text-zinc-900 dark:text-white' : 'text-zinc-700 dark:text-zinc-200'}`}>{label}</span>
                      <span className="text-[11px] text-zinc-400 dark:text-zinc-500">{desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <button
            onClick={() => handleNav(View.PRICING)}
            className={navLinkClass(currentView === View.PRICING)}
          >
            Pricing
          </button>

          {/* Community */}
          <button
            onClick={() => handleNav(View.COMMUNITY)}
            className={navLinkClass(currentView === View.COMMUNITY)}
          >
            Community
          </button>

          {/* Features */}
          <button
            onClick={() => handleNav(View.FEATURES)}
            className={navLinkClass(currentView === View.FEATURES)}
          >
            Features
          </button>

          {/* How it works */}
          <button
            onClick={() => handleNav(View.HOW_IT_WORKS)}
            className={`${navLinkClass(currentView === View.HOW_IT_WORKS)} whitespace-nowrap`}
          >
            How it works
          </button>

          {/* Generator */}
          <button
            onClick={() => handleNav(View.GENERATOR)}
            aria-current={currentView === View.GENERATOR ? 'page' : undefined}
            className={`${navLinkClass(currentView === View.GENERATOR)} flex items-center gap-2`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform duration-300 group-hover:rotate-45 group-hover:scale-125 text-amber-500 dark:text-amber-300"><path d="M12 2L14 10L22 12L14 14L12 22L10 14L2 12L10 10L12 2Z" fill="currentColor" stroke="none"/></svg>
            Generator
          </button>
        </nav>

        {/* Actions — right */}
        <div className={`fixed-header-actions flex items-center shrink-0 ${
          scrolled ? 'gap-2' : 'gap-2 sm:gap-3'
        }`}>
          {/* Dark Mode Toggle */}
          <motion.button
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            whileHover={{ scale: 1.12, rotate: isDark ? 15 : -15 }}
            whileTap={{ scale: 0.92 }}
            className={`group flex items-center justify-center rounded-full bg-zinc-100/60 hover:bg-zinc-100 dark:bg-white/5 dark:hover:bg-white/15 text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white shadow-sm hover:shadow-md transition-all duration-300 ${scrolled ? 'w-8 h-8' : 'w-9 h-9'}`}
          >
            {isDark ? (
              <svg className="transition-transform duration-300 group-hover:rotate-90" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22"/></svg>
            ) : (
              <svg className="transition-transform duration-300 group-hover:-rotate-12" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"/></svg>
            )}
          </motion.button>
          {isAuthenticated && onDashboard && (
            <motion.button
              onClick={onDashboard}
              aria-label="Open dashboard"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`group hidden md:flex items-center gap-2 font-bold text-zinc-900 hover:text-black bg-white border-2 border-zinc-900 hover:bg-zinc-50 hover:shadow-lg hover:shadow-zinc-900/10 dark:text-white dark:bg-transparent dark:border-white dark:hover:bg-white/10 rounded-full transition-all duration-300 ${scrolled ? 'px-3 py-1.5 text-xs' : 'px-5 py-2.5 text-sm'}`}
            >
              <svg className={`transition-transform duration-200 group-hover:scale-110 ${scrolled ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/>
                <rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/>
              </svg>
              Dashboard
            </motion.button>
          )}
          <Button
            variant={currentView === View.AUTH ? 'secondary' : 'primary'}
            size={scrolled ? 'sm' : 'md'}
            className="hidden sm:inline-flex transition-all duration-300"
            onClick={() => {
              setMobileMenuOpen(false);
              if (currentView === View.AUTH) onNavigate(View.LANDING);
              else if (isAuthenticated && onDashboard) onDashboard();
              else onNavigate(View.AUTH);
            }}
            aria-label={currentView === View.AUTH ? 'Close authentication panel' : isAuthenticated ? 'Open account dashboard' : 'Open sign in and sign up'}
          >
            {currentView === View.AUTH ? 'Close' : isAuthenticated ? 'My Account' : 'Get Started'}
          </Button>

          {/* Mobile Hamburger */}
          <button
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileMenuOpen((v) => !v)}
            className={`lg:hidden flex items-center justify-center rounded-xl hover:bg-zinc-100 dark:hover:bg-white/10 transition-all duration-300 ${scrolled ? 'w-8 h-8' : 'w-10 h-10'}`}
          >
            <div className="relative w-5 h-4 flex flex-col justify-between">
              <span className={`block h-0.5 w-full bg-zinc-900 dark:bg-white rounded transition-all duration-300 origin-center ${mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
              <span className={`block h-0.5 w-full bg-zinc-900 dark:bg-white rounded transition-all duration-300 ${mobileMenuOpen ? 'opacity-0 scale-x-0' : ''}`} />
              <span className={`block h-0.5 w-full bg-zinc-900 dark:bg-white rounded transition-all duration-300 origin-center ${mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
            </div>
          </button>
        </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Mobile Menu Drawer */}
      <nav
        className={`fixed top-0 right-0 z-40 h-full w-[92vw] max-w-sm bg-white dark:bg-zinc-900 shadow-2xl transform transition-transform duration-300 ease-out lg:hidden ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full pt-20 pb-8 px-6 overflow-y-auto">
          <div className="flex flex-col gap-1">
            <button
              onClick={() => handleNav(View.LUMI)}
              className={lumiMobileClass(currentView === View.LUMI)}
            >
              <span className="relative z-10 flex items-center gap-2">
                <span className={lumiLabelClass(currentView === View.LUMI)}>Lumi</span>
              </span>
            </button>
            <button onClick={() => handleNav(View.FEATURES)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10`}>
              Features
            </button>
            <button onClick={() => handleNav(View.COMMUNITY)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all ${currentView === View.COMMUNITY ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10'}`}>
              Community
            </button>
            <button onClick={() => handleNav(View.HOW_IT_WORKS)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10`}>
              How it works
            </button>
            <button onClick={() => handleNav(View.GENERATOR)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all flex items-center gap-2 ${currentView === View.GENERATOR ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10'}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L14 10L22 12L14 14L12 22L10 14L2 12L10 10L12 2Z" fill="currentColor" stroke="none"/></svg>
              Generator
            </button>
            {/* Information section */}
            <div className="mt-1 mb-0.5 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500">Information</div>
            <button onClick={() => handleNav(View.EXTENSIONS)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all flex items-center gap-2 ${currentView === View.EXTENSIONS ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10'}`}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a2 2 0 0 0 0-4h-1.26a6 6 0 0 0-.84-2.03l.9-.9a2 2 0 1 0-2.83-2.83l-.9.9A6 6 0 0 0 13 2.26V1a2 2 0 0 0-4 0v1.26a6 6 0 0 0-2.03.84l-.9-.9a2 2 0 1 0-2.83 2.83l.9.9A6 6 0 0 0 2.26 11H1a2 2 0 0 0 0 4h1.26a6 6 0 0 0 .84 2.03l-.9.9a2 2 0 1 0 2.83 2.83l.9-.9A6 6 0 0 0 11 21.74V23a2 2 0 0 0 4 0v-1.26a6 6 0 0 0 2.03-.84l.9.9a2 2 0 1 0 2.83-2.83l-.9-.9A6 6 0 0 0 21.74 13H23a2 2 0 0 0 0-4h-1.26Z"/><path d="M9.5 9.5h5v5h-5z"/></svg>
              Extensions
            </button>
            <button onClick={() => handleNav(View.DEMO)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all flex items-center gap-2 ${currentView === View.DEMO ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10'}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21" /></svg>
              Live Demo
            </button>
            <button onClick={() => handleNav(View.PRICING)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all ${currentView === View.PRICING ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10'}`}>
              Pricing
            </button>
            <button onClick={() => handleNav(View.DOCUMENTATION)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all ${currentView === View.DOCUMENTATION ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10'}`}>
              Documentation
            </button>
            <button onClick={() => handleNav(View.BLOG)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all ${currentView === View.BLOG ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10'}`}>
              Blog
            </button>
            <button onClick={() => handleNav(View.COMMANDS)} className={`w-full text-left px-4 py-3 text-sm font-bold rounded-xl transition-all ${currentView === View.COMMANDS ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10'}`}>
              Commands
            </button>
          </div>

          <div className="mt-auto flex flex-col gap-3 pt-6 border-t border-zinc-100 dark:border-zinc-800">
            {isAuthenticated && onDashboard && (
              <button
                onClick={() => { setMobileMenuOpen(false); onDashboard(); }}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold text-zinc-700 hover:text-black hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-white dark:hover:bg-white/10 rounded-xl transition-all border border-zinc-200 dark:border-zinc-700"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/>
                  <rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/>
                </svg>
                Dashboard
              </button>
            )}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                if (currentView === View.AUTH) onNavigate(View.LANDING);
                else if (isAuthenticated && onDashboard) onDashboard();
                else onNavigate(View.AUTH);
              }}
              className="w-full px-5 py-3 text-sm font-bold rounded-xl bg-zinc-900 text-white hover:bg-black dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200 transition-all"
            >
              {currentView === View.AUTH ? 'Close' : isAuthenticated ? 'My Account' : 'Get Started'}
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;
